import type { PackageDocType } from '../db/schemas';
import type { POSCartItem } from '../stores/pos.store';

export interface DetectedComboMatch {
  packageDoc: PackageDocType;
  matchingItemIds: string[];
  originalTotal: number;
  comboPrice: number;
  savings: number;
}

/**
 * Analiza los ítems del carrito y detecta si existen services sueltos que coincidan
 * con un Paquete/Combo is_active del catálogo ofreciendo un ahorro para el cliente.
 */
export function detectMatchingCombos(
  cartItems: POSCartItem[],
  packages: PackageDocType[]
): DetectedComboMatch | null {
  if (!cartItems || cartItems.length < 2 || !packages || packages.length === 0) {
    return null;
  }

  // Filtrar solo ítems de servicio individuales (excluir packages ya convertidos)
  const serviceItems = cartItems.filter(
    (item) => item.service && item.type !== 'package'
  );

  if (serviceItems.length < 2) return null;

  // Mapa de service_id -> lista de ítems del carrito
  const cartServiceIds = serviceItems.map((item) => item.service!.id);

  for (const pkg of packages) {
    if (!pkg.items || pkg.items.length < 2) continue;

    // Verificar si todos los service_id del combo están presentes en los ítems del carrito
    const requiredServiceIds = pkg.items.map((i) => i.service_id);
    const hasAllServices = requiredServiceIds.every((reqId) =>
      cartServiceIds.includes(reqId)
    );

    if (hasAllServices) {
      // Identificar los IDs locales del carrito que corresponden a los services del combo
      const matchingItemIds: string[] = [];
      let originalTotal = 0;

      for (const reqId of requiredServiceIds) {
        const found = serviceItems.find(
          (item) => item.service!.id === reqId && !matchingItemIds.includes(item.id)
        );
        if (found) {
          matchingItemIds.push(found.id);
          originalTotal += found.unitPrice * found.quantity;
        }
      }

      const comboPrice = pkg.currency_price || 0;
      const savings = originalTotal - comboPrice;

      // Solo proponer si efectivamente representa un ahorro o mejor price para el cliente
      if (savings > 0 && matchingItemIds.length === requiredServiceIds.length) {
        return {
          packageDoc: pkg,
          matchingItemIds,
          originalTotal,
          comboPrice,
          savings,
        };
      }
    }
  }

  return null;
}
